/**
 * Раздел «Банк» (этап 3, Б2): банковские выписки и поступления от клиентов.
 * Паспорт — docs/features/client-payments.md.
 */
import type { SellerEntity } from './orders'
import type { Role } from './roles'

/** Кто работает с разделом: загружает выписки и разбирает строки. */
export const BANK_EDIT_ROLES: Role[] = ['ADMIN', 'ACCOUNTANT']
/** Кто видит раздел (генеральный директор — только просмотр). */
export const BANK_VIEW_ROLES: Role[] = ['ADMIN', 'GENERAL_DIRECTOR', 'ACCOUNTANT']

/**
 * Что это за строка выписки. К разносу по заказам идёт только INCOMING;
 * остальное программа сама помечает «не учитываем».
 */
export const BANK_LINE_KINDS = ['INCOMING', 'OUTGOING', 'DEPOSIT', 'REFUND'] as const
export type BankLineKind = (typeof BANK_LINE_KINDS)[number]

export const BANK_LINE_KIND_LABELS_RU: Record<BankLineKind, string> = {
  INCOMING: 'Поступление',
  OUTGOING: 'Списание',
  DEPOSIT: 'Депозит',
  REFUND: 'Возврат',
}

/**
 * PENDING — ждёт разноса; PARTIAL — разнесена часть суммы, остаток ждёт (переплата,
 * заказ ещё не заведён); ALLOCATED — разнесена целиком; SKIPPED — не учитываем
 * (можно вернуть в работу).
 */
export const BANK_LINE_STATUSES = ['PENDING', 'PARTIAL', 'ALLOCATED', 'SKIPPED'] as const
export type BankLineStatus = (typeof BANK_LINE_STATUSES)[number]

export const BANK_LINE_STATUS_LABELS_RU: Record<BankLineStatus, string> = {
  PENDING: 'К разносу',
  PARTIAL: 'Разнесено частично',
  ALLOCATED: 'Разнесено',
  SKIPPED: 'Не учитываем',
}

/**
 * Почему программа сама поставила строке «не учитываем» (14.10.2026). BEFORE_START —
 * оплата по счёту, выставленному до даты начала учёта в разделе «Банк»: прошлый
 * период сверяется в 1С, в микрохабе его не разносим. Ручная смена статуса причину снимает.
 */
// MANUAL_PENDING — строку, убранную по дате начала учёта, бухгалтер вернул «К разносу»
// сам: пересчёт по дате её больше не убирает (аудит 20.10.2026).
export const BANK_SKIP_REASONS = ['BEFORE_START', 'MANUAL_PENDING'] as const
export type BankSkipReason = (typeof BANK_SKIP_REASONS)[number]

export const BANK_SKIP_REASON_LABELS_RU: Record<BankSkipReason, string> = {
  BEFORE_START: 'Счёт до начала учёта',
  MANUAL_PENDING: 'Возвращено в работу вручную',
}

/**
 * Поступление относится к периоду до начала учёта: дата поступления раньше даты
 * начала, либо все счета в назначении платежа датированы раньше неё. Счёт без даты
 * («по счёту 1298») решить не даёт — строка остаётся в работе. Даты — `YYYY-MM-DD`.
 */
export function isBeforeAccountingStart(
  line: { postedAt: string | null; invoiceRefs: readonly { date: string | null }[] },
  startDate: string | null,
): boolean {
  if (!startDate) return false
  if (line.postedAt && line.postedAt < startDate) return true
  if (line.invoiceRefs.length === 0) return false
  return line.invoiceRefs.every((r) => r.date != null && r.date < startDate)
}

/** Строка ещё в очереди «не разнесено». */
export const BANK_LINE_OPEN_STATUSES: readonly BankLineStatus[] = ['PENDING', 'PARTIAL']

/** Что бухгалтер может выставить строке вручную; остальное ставит разнос. */
export const BANK_LINE_MANUAL_STATUSES = ['PENDING', 'SKIPPED'] as const

/** Статус строки по разнесённой сумме (расхождение до копейки — не остаток). */
export function bankLineStatusByAmount(amount: number, allocated: number): BankLineStatus {
  if (allocated <= 0.005) return 'PENDING'
  return allocated >= amount - 0.005 ? 'ALLOCATED' : 'PARTIAL'
}

/** Откуда оплата по заказу: внесли руками или создал разнос выписки. */
export const ORDER_PAYMENT_SOURCES = ['MANUAL', 'BANK'] as const
export type OrderPaymentSource = (typeof ORDER_PAYMENT_SOURCES)[number]

export const ORDER_PAYMENT_SOURCE_LABELS_RU: Record<OrderPaymentSource, string> = {
  MANUAL: 'Вручную',
  BANK: 'Из банка',
}

/**
 * Что программа решила про поступление (решения Сергея 09–11.10.2026):
 *  AUTO — разносится само: клиент найден по ИНН, номер счёта совпал;
 *  CONFIRM — нужен один клик: номера в заказе нет, но заказ на эту сумму один,
 *            либо в заказе уже есть ручная оплата на эту сумму с другой датой;
 *  CHOOSE — бухгалтер делит сумму по заказам сам;
 *  NO_CLIENT — клиента с таким ИНН в программе нет.
 */
export const BANK_MATCH_STATES = ['AUTO', 'CONFIRM', 'CHOOSE', 'NO_CLIENT'] as const
export type BankMatchState = (typeof BANK_MATCH_STATES)[number]

export const BANK_MATCH_STATE_LABELS_RU: Record<BankMatchState, string> = {
  AUTO: 'Разнесётся само',
  CONFIRM: 'Нужно подтвердить',
  CHOOSE: 'Нужно выбрать',
  NO_CLIENT: 'Клиент не найден',
}

/** Статус строки сразу после загрузки: поступление — к разносу, прочее — не учитываем. */
export function defaultBankLineStatus(kind: BankLineKind): BankLineStatus {
  return kind === 'INCOMING' ? 'PENDING' : 'SKIPPED'
}

/**
 * Приставка номера счёта 1С → юрлицо (подтвердил Сергей 12.10.2026 по реестру
 * счетов за сентябрь): «сэНФ-001298» — СПМ, «ДИНФ-000055» — ДИСТИНКТ. Приставка
 * МОСЭЛ пока неизвестна — вопрос бухгалтерии; счёт с незнакомой приставкой
 * не сопоставляется. Ключи — в нижнем регистре.
 */
export const INVOICE_PREFIX_ENTITY: Record<string, SellerEntity> = {
  сэнф: 'SPM',
  динф: 'DISTINCT',
}

/** Юрлицо по номеру счёта 1С («сэНФ-001298» → SPM); незнакомая приставка — null. */
export function entityByInvoiceNumber(number: string): SellerEntity | null {
  const prefix =
    number
      .trim()
      .match(/^([^\d\s-]+)/)?.[1]
      ?.toLowerCase() ?? ''
  return INVOICE_PREFIX_ENTITY[prefix] ?? null
}
